import { mdiDatabaseOutline } from '@mdi/js';
import React, { CSSProperties, Dispatch } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    IEplassProjectData,
    setActiveCategory,
    setOpenNodes,
} from '../../../../redux/user/sliceUserProjects';
import { IAppReduxStore } from '../../../../redux/store';
import DynamicTree from '../../../General/DynamicTree/DynamicTree';
import {
    DynamicTreeItemCategory,
    ITreeItemCategoryProps,
} from '../../../General/DynamicTree/DynamicTreeItemCategory';
import { buildTree, ITreeNode, sortData } from '../../../General/DynamicTree/DynamicTreeUtils';
import ProjectContextMenu, { IProjectContextMenuConfig } from './ProjectContextMenu';
import { ITreeItemProjectProps, TreeItemProject } from './ProjectTreeItem';
import { filterProjects } from './ProjectUtils';

/**
 * Component that shows the category structure of a user's projects.
 */
export default (props: IProjectTreeProps) => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Allows the component to navigate to other views in the application.  */
    const navigate = useNavigate();

    /** Get relevant values from Redux store. */
    const activeCategory = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.activeCategory,
    );
    const expanded = useSelector((store: IAppReduxStore) => store.userProjects.settings.openNodes);
    const filter = useSelector((store: IAppReduxStore) => store.userProjects.settings.filter);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);
    const viewType = useSelector((store: IAppReduxStore) => store.userProjects.settings.viewType);
    const favourites = useSelector(
        (store: IAppReduxStore) => store.userSettings.data?.favourites?.projects,
    );

    /** Get an array of the selected categories in the TreeView. */
    const selected = activeCategory
        ? activeCategory.split('\\').map((_, i, arr) => arr.slice(0, i + 1).join('\\'))
        : [];

    /**
     * Function that allows the component to update the active category in the redux store.
     * @param category - New active cagtegory.
     */
    const updateActiveCategory = (category: string) => dispatch(setActiveCategory(category));

    /** Deconstruct props. */
    const { projectConfig, style, sortAz, contextMenu } = props;

    /** Create the tree structure for the tree's contents. */
    const tree = buildTree<IEplassProjectData>(
        filterProjects(projects, favourites, filter, viewType),
        'category',
        '\\',
    );

    /**
     * Creates the props object for a TreeItemProject.
     * @param project - Target project object.
     */
    const getRowProps = (project: IEplassProjectData): ITreeItemProjectProps => ({
        itemId: project.replicaId,
        labelIcon: mdiDatabaseOutline,
        labelText: project.title,
        module: project.module,
        favourite: projectConfig.favourites.indexOf(project.replicaId) >= 0,
        contextMenuConfig: {
            callback: e => contextMenu.callback(e, project),
            position: contextMenu.position,
            project: project,
        },
    });

    /**
     * Gets page prop values for a given TreeNode.
     * @param node - Target TreeNode.
     */
    const getProps = (node: ITreeNode<IEplassProjectData>) => ({
        key: node.path,
        itemId: node.path,
        labelText: node.title,
    });

    /**
     * Creates a new TreeItemCategory instance.
     * @param node - Target node object.
     */
    const categoryTreeItem = (node: ITreeNode<IEplassProjectData>, paddingLeft: number) => {
        const itemProps: ITreeItemCategoryProps & { paddingLeft: number } = {
            ...getProps(node),
            active: !!activeCategory && activeCategory.startsWith(node.path),
            onClick: () => updateActiveCategory(node.path),
            count: node.data.length,
            compact: true,
            paddingLeft,
        };

        return (
            <DynamicTreeItemCategory {...itemProps}>
                {sortData<IEplassProjectData, string>(node.categories, 'title', {
                    stringsAZ: sortAz,
                    datesDesc: sortAz,
                }).map(x => categoryTreeItem(node.categories[x], paddingLeft + 8))}
            </DynamicTreeItemCategory>
        );
    };

    /**
     * Creates a new TreeItemCategory instance.
     * @param node - Target node object.
     */
    const projectTreeItem = (node: ITreeNode<IEplassProjectData>) => {
        const itemProps: ITreeItemProjectProps & { key: string } = {
            ...getProps(node),
            onClick: !projectConfig ? () => updateActiveCategory(node.path) : null,
            contextMenuConfig: contextMenu,
        };
        const { key, ...otherProps } = itemProps;

        return (
            <TreeItemProject key={key} {...otherProps}>
                {sortData<IEplassProjectData, string>(node.categories, 'title', {
                    stringsAZ: sortAz,
                    datesDesc: sortAz,
                }).map(x => projectTreeItem(node.categories[x]))}

                {sortData<IEplassProjectData, IEplassProjectData>(node.data, 'title', {
                    stringsAZ: sortAz,
                    datesDesc: sortAz,
                }).map(proj => (
                    <TreeItemProject
                        key={proj.replicaId}
                        onClick={() => navigate(`/project/${proj.replicaId}`)}
                        {...getRowProps(proj)}
                    />
                ))}
            </TreeItemProject>
        );
    };

    /**
     * Updates the list of open nodes in the tree.
     * @param _ - This parameter is not used.
     * @param itemIds - Array of expanded node ids
     */
    const onExpandedItemsChange = (_: React.SyntheticEvent, itemIds: string[]) =>
        dispatch(setOpenNodes(itemIds));

    /** Create the tree's project rows. */
    const rows = sortData<IEplassProjectData, string>(tree.categories, 'title', {
        stringsAZ: sortAz,
        datesDesc: sortAz,
    }).map(c =>
        projectConfig
            ? projectTreeItem(tree.categories[c])
            : categoryTreeItem(tree.categories[c], 4),
    );

    return (
        <DynamicTree {...{ onExpandedItemsChange, expanded, selected, style }}>
            {rows}
            <ProjectContextMenu config={contextMenu} />
        </DynamicTree>
    );
};

/**
 * Interface that describes the properties for the ProjectTree Component.
 */
export interface IProjectTreeProps {
    /** Sorting direction. */
    sortAz: boolean;

    /** (Optional) Project View Configuration. */
    projectConfig?: {
        /** List of user project favourites. */
        favourites: string[];
    };

    /** (Optional) Additional CSS styles. */
    style?: CSSProperties;

    /** Context menu configuration. */
    contextMenu?: IProjectContextMenuConfig;
}
